<template>
  <div>
    <v-dialog
      v-model="open"
      persistent
      scrollable
      max-width="60vw"
      content-class="rounded-dialog"
    >
      <v-card :class="classes" :style="styles + ';' + gradient">
        <v-progress-linear
          :indeterminate="true"
          v-if="processing"
          style="position: absolute; top: 0; left: 0; right: 0; z-index: 1000;"
          :color="primaryColor"
        ></v-progress-linear>

        <view-text
          :settings="component.dialog.opportunityTitle"
          :value="opportunity.title"
        ></view-text>

        <!-- Alerts section - moved from evaluation tab -->
        <v-alert
          v-if="hasError"
          color="error"
          class="mx-4 mt-2 mb-1"
          dense
          text
        >
          {{ errorMessage || "There was an error processing the form data." }}
        </v-alert>

        <v-alert
          v-if="isTableBeingEdited"
          color="error"
          class="mx-4 mt-2 mb-1"
          dense
          text
        >
          Please save or cancel your current table edits before submitting the
          form.
        </v-alert>

        <!-- Stepper for multi-page forms -->
        <v-stepper v-if="showStepper" v-model="currentStep" class="mx-n4">
          <v-stepper-header>
            <!-- Render steps and dividers separately -->
            <v-stepper-step
              v-for="(page, index) in formPages"
              :key="`step-${index}`"
              :complete="currentStep > index + 1"
              :step="index + 1"
              color="primary"
            >
              {{ page.title || `Page ${index + 1}` }}
            </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content
              v-for="(page, pageIndex) in formPages"
              :key="`content-${pageIndex}`"
              :step="pageIndex + 1"
            >
              <div>
                <page
                  :component="component"
                  :page="page"
                  :pageIndex="pageIndex"
                  :formValues="formValues"
                  :validationErrors="validationErrors"
                  :hasAttemptedSubmit="
                    stepValidationAttempted[pageIndex] || hasAttemptedSubmit
                  "
                  @update:form-value="updateFormValue"
                  @table-edit-state-change="handleTableEditStateChange"
                  @clear-validation="handleClearValidation"
                  @validation-event="handleValidationEvent"
                  @validation-error="handleValidationError"
                  @upload-status-change="handleFileUploadStatusChange"
                />
              </div>

              <v-row no-gutters>
                <v-btn
                  color="grey darken-1"
                  text
                  @click="closeDialog"
                  :disabled="processing"
                  >Cancel</v-btn
                >
                <v-spacer></v-spacer>
                <v-btn
                  v-if="pageIndex > 0"
                  text
                  elevation="0"
                  @click="currentStep--"
                  :disabled="isTableBeingEdited"
                >
                  Back
                </v-btn>
                <v-btn
                  v-if="pageIndex < formPages.length - 1"
                  :color="buttonColor"
                  :class="buttonTextColor"
                  elevation="0"
                  @click="validateAndProceed(pageIndex)"
                  :disabled="isTableBeingEdited || fileUploadsInProgress"
                >
                  Next
                </v-btn>
                <v-btn
                  v-if="pageIndex === formPages.length - 1"
                  :color="buttonColor"
                  :class="buttonTextColor"
                  elevation="0"
                  @click="submitForm"
                  :loading="processing"
                  :disabled="!isFormSubmittable"
                >
                  Submit Form
                </v-btn>
              </v-row>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

        <!-- Single page display when no stepper -->
        <div v-if="!showStepper">
          <page
            v-for="(page, pageIndex) in formPages"
            :key="`page-${pageIndex}`"
            :component="component"
            :page="page"
            :pageIndex="pageIndex"
            :formValues="formValues"
            :validationErrors="validationErrors"
            :hasAttemptedSubmit="
              stepValidationAttempted[pageIndex] || hasAttemptedSubmit
            "
            @update:form-value="updateFormValue"
            @table-edit-state-change="handleTableEditStateChange"
            @clear-validation="handleClearValidation"
            @validation-event="handleValidationEvent"
            @validation-error="handleValidationError"
            @upload-status-change="handleFileUploadStatusChange"
          />

          <v-card-actions>
            <v-btn
              color="grey darken-1"
              text
              @click="closeDialog"
              :disabled="processing"
              >Cancel</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
              :color="buttonColor"
              :class="buttonTextColor"
              @click="submitForm"
              :loading="processing"
              :disabled="!isFormSubmittable"
              elevation="0"
            >
              Submit Form
            </v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Page from "./Components/Page.vue";
import FormDataProcessor from "@/utils/FormDataProcessor";
import DateUtils from "@/utils/DateUtils";
import ViewText from "@/components/Design/View/ViewComponents/Shared/Text.vue";
import { db } from "@/firebase";
import {
  serverTimestamp,
  doc,
  getDoc,
  collection,
  getDocs
} from "firebase/firestore";
export default {
  components: {
    Page,
    ViewText
  },
  props: {
    open: Boolean,
    previewArr: Array,
    component: {
      type: Object,
      required: true,
      default: () => ({})
    },
    opportunity: Object,
    memberId: {
      type: String,
      required: true
    },
    opportunityId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      hasAttemptedSubmit: false, // Add this flag to track submission attempts
      stepValidationAttempted: {}, // Track validation attempts per step
      processing: false,
      currentStep: 1,
      formPages: [],
      formValues: {},
      structuredFormValues: {},
      fieldInfoMap: {}, // Maps field_x_y_z keys to field objects with extended properties
      dynamicOptionsCache: {}, // Cache of dynamic options for each field
      tablesBeingEdited: [], // Track which tables are being edited
      fileUploadsInProgress: false, // Track if any file uploads are in progress
      formSubmissionMeta: {
        submittedAt: null,
        formId: null,
        formTitle: null,
        formVersion: "1.0"
      },
      presetRecordsCache: {}, // Add cache for preset records
      validationErrors: {}, // Add validationErrors data property
      errorMessage: "",
      hasError: false,
      formPoints: 0,
      history: [],
      id: null,
      points: 0,
      totalPoints: 0,
      status: "Pending",
      reviewed: null,
      company: null,
      entryId: null,
      validationEvents: [] // Array to collect successful validation events
    };
  },
  computed: {
    buttonColor() {
      if (
        this.component.dialog.button &&
        this.component.dialog.button.color &&
        this.component.dialog.button.color.hexa
      ) {
        return this.component.dialog.button.color.hexa;
      } else {
        return this.$store.state.program.currentProgram.webTheme.colors.primary
          .hexa;
      }
    },
    buttonTextColor() {
      if (this.component.dialog.button && this.component.dialog.button.dark) {
        return this.component.dialog.button.dark;
      } else {
        return "white--text";
      }
    },
    classes() {
      if (this.component.dialog.fullscreen === true) return "rounded-0";
      return this.component.card.rounded;
    },
    primaryColor() {
      return this.$store.state.program.currentProgram.webTheme.colors.primary
        .hexa;
    },
    styles() {
      // Helper function to ensure minimum padding
      const ensureMinPaddingX = value => Math.max(28, value);
      const ensureMinPaddingY = value => Math.max(20, value);

      if (this.component.dialog.fullscreen === true) return "height: 100%";

      var styles =
        "padding-bottom: " +
        ensureMinPaddingY(this.component.dialog.padding?.bottom || 20) +
        "px ;" +
        "padding-top: " +
        ensureMinPaddingY(this.component.dialog.padding?.top || 20) +
        "px ;" +
        "padding-left: " +
        ensureMinPaddingX(this.component.dialog.padding?.left || 28) +
        "px ;" +
        "padding-right: " +
        ensureMinPaddingX(this.component.dialog.padding?.right || 28) +
        "px ;" +
        "border: " +
        (this.component.dialog.borderSize || 0) +
        "px solid " +
        (this.component.dialog.borderColor?.hexa || "transparent") +
        ";";

      return styles;
    },
    gradient() {
      if (
        this.component.dialog.color &&
        this.component.dialog.color.type === "gradient"
      ) {
        var color = this.component.dialog.color;
        var gradient =
          "background: linear-gradient(" +
          color.direction +
          ", rgba(" +
          color.gradientFrom.rgba.r +
          ", " +
          color.gradientFrom.rgba.g +
          ", " +
          color.gradientFrom.rgba.b +
          ", " +
          color.gradientFrom.rgba.a +
          ") " +
          ", rgba(" +
          color.gradientTo.rgba.r +
          ", " +
          color.gradientTo.rgba.g +
          ", " +
          color.gradientTo.rgba.b +
          ", " +
          color.gradientTo.rgba.a +
          ")" +
          color.gradientHeight +
          "%)";
        return gradient;
      } else if (
        this.component.dialog.color &&
        this.component.dialog.color.solidColor
      ) {
        return (
          "background: " + this.component.dialog.color.solidColor.hexa + ";"
        );
      } else {
        return "";
      }
    },
    showStepper() {
      return (
        this.formPages.length > 1 &&
        this.$store.state.offer &&
        this.$store.state.offer.currentOffer &&
        this.$store.state.offer.currentOffer.form &&
        this.$store.state.offer.currentOffer.form.stepper
      );
    },

    currentFormId() {
      return this.$store.state.offer && this.$store.state.offer.currentOffer
        ? this.$store.state.offer.currentOffer.id
        : null;
    },

    currentFormTitle() {
      return this.$store.state.offer && this.$store.state.offer.currentOffer
        ? this.$store.state.offer.currentOffer.title
        : null;
    },

    isTableBeingEdited() {
      return this.tablesBeingEdited.length > 0;
    },

    isFormSubmittable() {
      // Form is not submittable if:
      // 1. Any table is being edited
      // 2. We're already processing the form submission
      // 3. Any file upload is in progress
      // 4. There are validation errors
      return (
        !this.isTableBeingEdited &&
        !this.processing &&
        !this.fileUploadsInProgress &&
        Object.keys(this.validationErrors).length === 0
      );
    },

    formTitle() {
      if (this.currentFormTitle) {
        return this.currentFormTitle;
      } else if (this.formPages.length > 0 && this.formPages[0].title) {
        return this.formPages[0].title;
      }
      return "Form Entry";
    }
  },
  watch: {
    previewArr: {
      handler(newVal) {
        // Create a deep copy when the prop changes
        this.formPages = JSON.parse(JSON.stringify(newVal));
        // Initialize form values structure and build field map
        this.initializeFormValues();
      },
      immediate: true
    }
  },
  created() {
    // Listen for dynamic option updates
    this.$root.$on("dynamic-options-loaded", this.handleDynamicOptionsLoaded);
  },
  beforeDestroy() {
    // Remove event listener
    this.$root.$off("dynamic-options-loaded", this.handleDynamicOptionsLoaded);
  },
  methods: {
    // Add handler for file upload status change events
    handleFileUploadStatusChange(isUploading) {
      this.fileUploadsInProgress = isUploading;
    },

    closeDialog() {
      this.$emit("onCancel");
      this.resetForm();
      this.clearAlertData();
    },

    // Helper method to clear alert data
    clearAlertData() {
      this.hasError = false;
      this.errorMessage = "";
      this.formPoints = 0;
      // Reset validation events for a clean state
      this.validationEvents = [];
    },

    resetForm() {
      this.currentStep = 1;
      this.initializeFormValues();
      this.structuredFormValues = {};
      this.validationErrors = {};
      // Reset validation events when form is reset
      this.validationEvents = [];
    },

    generateUniqueId() {
      return (
        "field-" +
        Math.random()
          .toString(36)
          .substring(2, 10)
      );
    },

    handleDynamicOptionsLoaded(data) {
      if (data && data.fieldId && Array.isArray(data.options)) {
        this.dynamicOptionsCache[data.fieldId] = data.options;
        if (this.fieldInfoMap[data.fieldId]) {
          this.fieldInfoMap[data.fieldId].dynamicOptions = data.options;
        }
      }
    },

    initializeFormValues() {
      // Reset form values and field map
      this.formValues = {};
      this.fieldInfoMap = {};
      this.dynamicOptionsCache = {};
      // Reset validation events when initializing form
      this.validationEvents = [];

      // Create structure for storing form values and build field map
      this.formPages.forEach((page, pageIndex) => {
        page.sections.forEach((section, sectionIndex) => {
          section.columns.forEach((column, columnIndex) => {
            column.items.forEach((field, fieldIndex) => {
              const fieldId = `field_${pageIndex}_${sectionIndex}_${columnIndex}_${fieldIndex}`;

              // Skip certain field types that don't need values
              if (["title", "subtitle", "note"].includes(field.type)) {
                return;
              }

              // Ensure field has an idx
              if (!field.idx) {
                field.idx = this.generateUniqueId();
              }

              // Process table component fields
              if (
                field.type === "table" &&
                Array.isArray(field.componentFields)
              ) {
                field.componentFields = field.componentFields.map(compField => {
                  if (!compField.idx) {
                    compField.idx = `table-field-${this.generateUniqueId()}`;
                  }
                  return compField;
                });
              }

              // Store field info
              this.fieldInfoMap[fieldId] = {
                fieldId: fieldId,
                idx: field.idx,
                label: field.label || `Field ${fieldIndex}`,
                type: field.type || "text",
                placeholder: field.placeholder,
                required: field.required || false,
                clearable: field.clearable,
                validation: field.validation,
                entryLimits: field.entryLimits,
                position: {
                  page: pageIndex,
                  section: sectionIndex,
                  column: columnIndex,
                  index: fieldIndex
                },
                presets: this.processFieldPresets(field),
                min: field.min,
                max: field.max,
                multiple: field.multiple,
                prependIcon: field.prependIcon,
                thumbLabel: field.thumbLabel,
                componentFields: this.processComponentFields(
                  field,
                  pageIndex,
                  sectionIndex,
                  columnIndex
                ),
                styles: field.styles
              };

              // Set initial value based on field type
              if (field.type === "checkbox" || field.type === "checkboxes") {
                this.$set(this.formValues, fieldId, []);
              } else if (field.type === "switch") {
                this.$set(this.formValues, fieldId, false);
              } else if (field.type === "table") {
                this.$set(this.formValues, fieldId, [{}]);
              } else if (field.type === "radio") {
                this.$set(this.formValues, fieldId, "");
              } else if (field.type === "facebook") {
                this.$set(this.formValues, fieldId, {
                  url: "",
                  screenshot: null
                });
              } else if (field.type === "dropzone") {
                this.$set(this.formValues, fieldId, []);
              } else {
                this.$set(this.formValues, fieldId, "");
              }
            });
          });
        });
      });
    },

    processFieldPresets(field) {
      if (!field.presets) return null;

      const presets = JSON.parse(JSON.stringify(field.presets));

      if (
        presets.presetsType === "static" &&
        Array.isArray(presets.staticOptions)
      ) {
        presets.staticOptions = presets.staticOptions.map(option => {
          if (typeof option === "string") {
            return { value: option, text: option, identifier: option };
          } else if (typeof option === "object") {
            return {
              value: option.value || option.text || "",
              text: option.text || option.value || "",
              identifier:
                option.identifier || option.value || option.text || "",
              ...option
            };
          }
          return option;
        });
      }

      return presets;
    },

    processComponentFields(field, pageIndex, sectionIndex, columnIndex) {
      if (!field.componentFields) return null;

      return field.componentFields.map((componentField, fieldIndex) => {
        const componentFieldId = `field_${pageIndex}_${sectionIndex}_${columnIndex}_${fieldIndex}_component`;
        const componentFieldIdx =
          componentField.idx || `table-field-${this.generateUniqueId()}`;

        if (
          componentField.type === "dropdown" &&
          componentField.presets &&
          componentField.presets.presetsType === "static"
        ) {
          if (Array.isArray(componentField.presets.staticOptions)) {
            componentField.presets.staticOptions = componentField.presets.staticOptions.map(
              option => {
                if (typeof option === "object") {
                  return {
                    value: option.value || "",
                    text: option.value || "",
                    title: option.value || "",
                    identifier: option.value || "",
                    ...option
                  };
                }
                return option;
              }
            );
          }
        }

        return {
          fieldId: componentFieldId,
          idx: componentFieldIdx,
          label: componentField.label || "",
          type: componentField.type || "text",
          placeholder: componentField.placeholder,
          required: componentField.required || false,
          clearable: componentField.clearable,
          presets: this.processFieldPresets(componentField),
          active:
            componentField.active !== undefined ? componentField.active : true,
          cols: componentField.cols || 3
        };
      });
    },

    updateFormValue(fieldId, value) {
      const fieldInfo = this.fieldInfoMap[fieldId];
      this.$set(this.formValues, fieldId, value);

      // Clear validation error for this field
      if (this.validationErrors[fieldId]) {
        const isValueEmpty =
          value === null || value === undefined || value === "";

        if (!fieldInfo || !fieldInfo.required || !isValueEmpty) {
          this.$delete(this.validationErrors, fieldId);

          // Re-check validation state for the form as a whole
          if (
            this.hasError &&
            Object.keys(this.validationErrors).length === 0
          ) {
            this.hasError = false;
            this.errorMessage = "";

            // Also reset attempted submit if all errors have been fixed
            if (
              this.hasAttemptedSubmit &&
              Object.keys(this.validationErrors).length === 0
            ) {
              this.$store.commit("setSnackbar", null);
            }
          }
        }
      }

      if (fieldInfo && fieldInfo.type === "radio" && fieldInfo.allOptions) {
        fieldInfo.allOptions.forEach(opt => {
          opt.isSelected = opt.value === value;
        });
      }
    },

    buildFormStructure() {
      Object.keys(this.dynamicOptionsCache).forEach(fieldId => {
        if (this.fieldInfoMap[fieldId]) {
          this.fieldInfoMap[fieldId].dynamicOptions = this.dynamicOptionsCache[
            fieldId
          ];
        }
      });

      const fields = Object.values(this.fieldInfoMap);

      return {
        fields,
        formId: this.currentFormId,
        formTitle: this.currentFormTitle,
        formVersion: "1.0"
      };
    },

    transformFormData() {
      this.formSubmissionMeta = {
        submittedAt: new Date().toISOString(),
        formId: this.currentFormId,
        formTitle: this.currentFormTitle,
        formVersion: "1.0"
      };

      const formStructure = this.buildFormStructure();
      this.structuredFormValues = FormDataProcessor.transformToStructuredData(
        this.formValues,
        formStructure
      );

      // Process file resources from field_x_y_z objects in the entry
      this.formPages.forEach((page, pageIndex) => {
        page.sections.forEach((section, sectionIndex) => {
          section.columns.forEach((column, columnIndex) => {
            column.items.forEach((field, fieldIndex) => {
              const fieldId = `field_${pageIndex}_${sectionIndex}_${columnIndex}_${fieldIndex}`;

              // If this is a dropzone field, get the resources from the entry
              if (field.type === "dropzone" && this.entryId) {
                // Look for resources stored in the entry
                const entriesCollection =
                  this.$store.state.offer.currentOffer?.entries || [];
                const currentEntry = entriesCollection.find(
                  entry => entry.id === this.entryId
                );

                if (
                  currentEntry &&
                  currentEntry[fieldId] &&
                  currentEntry[fieldId].resources
                ) {
                  // Add the resources to the form values
                  this.$set(
                    this.formValues,
                    fieldId,
                    currentEntry[fieldId].resources
                  );

                  // Also update the structured values
                  if (field.idx && this.structuredFormValues[field.idx]) {
                    this.structuredFormValues[field.idx].value =
                      currentEntry[fieldId].resources;
                  }
                }
              }
            });
          });
        });
      });

      // Sanitize value helper function
      const sanitizeValue = value => {
        if (value === undefined) return null;
        if (Array.isArray(value)) {
          return value.map(v => sanitizeValue(v));
        }
        if (value && typeof value === "object") {
          const sanitized = {};
          Object.entries(value).forEach(([key, val]) => {
            sanitized[key] = sanitizeValue(val);
          });
          return sanitized;
        }
        return value;
      };

      // Sanitize all values in the structured form data
      Object.keys(this.structuredFormValues).forEach(key => {
        const field = this.structuredFormValues[key];
        if (field && typeof field === "object") {
          Object.keys(field).forEach(fieldKey => {
            field[fieldKey] = sanitizeValue(field[fieldKey]);
          });
        }

        if (field.type === "radio" && field.allOptions) {
          field.allOptions = field.allOptions.map(opt => ({
            ...opt,
            isSelected: opt.value === field.value
          }));
        }
      });
    },

    async submitForm() {
      this.processing = true;
      this.hasError = false;
      this.errorMessage = "";

      // Add this line to immediately trigger validation display
      this.hasAttemptedSubmit = true;

      try {
        // Don't validate if any tables are currently being edited
        if (this.tablesBeingEdited.length > 0) {
          this.$store.commit(
            "setSnackbar",
            "Please save or cancel your current table edits before submitting the form."
          );
          this.processing = false;
          return;
        }

        // Validate all fields before submission
        let hasError = false;
        for (const page of this.formPages) {
          for (const section of page.sections) {
            for (const column of section.columns) {
              for (const field of column.items) {
                const fieldId = `field_${this.formPages.indexOf(
                  page
                )}_${page.sections.indexOf(section)}_${section.columns.indexOf(
                  column
                )}_${column.items.indexOf(field)}`;

                // Skip validation if this field is a table that's currently being edited
                if (this.tablesBeingEdited.includes(fieldId)) {
                  continue;
                }

                const value = this.formValues[fieldId];

                // Skip validation for non-input fields
                if (["title", "subtitle", "note"].includes(field.type)) {
                  continue;
                }

                // Special handling for table fields
                if (field.type === "table") {
                  // Get table data
                  const tableData = this.formValues[fieldId];

                  if (
                    tableData &&
                    Array.isArray(tableData) &&
                    tableData.length > 0
                  ) {
                    // Validate each row in the table
                    for (
                      let rowIndex = 0;
                      rowIndex < tableData.length;
                      rowIndex++
                    ) {
                      const row = tableData[rowIndex];

                      // Check each field in the table row
                      if (
                        field.componentFields &&
                        Array.isArray(field.componentFields)
                      ) {
                        for (const tableField of field.componentFields) {
                          // Check if this table field has dynamic validation
                          if (
                            tableField.validationType === "dynamic" &&
                            tableField.validationDataSet
                          ) {
                            const tableFieldId =
                              tableField.idx || `field-${tableField.label}`;
                            const tableFieldValue = row[tableFieldId];

                            if (tableFieldValue) {
                              try {
                                const valueToValidate = this.getIdentifierFromValue(
                                  tableFieldValue
                                );

                                // Use the same validation logic as regular fields
                                const validationResult = await this.$store.dispatch(
                                  "validateTextFieldValue",
                                  {
                                    dataSetId: tableField.validationDataSet,
                                    value: valueToValidate,
                                    maxClaims: tableField.maxClaims || null,
                                    fieldLabel:
                                      `${field.label} - ${tableField.label}` ||
                                      "Table Field"
                                  }
                                );

                                if (!validationResult.isValid) {
                                  this.$set(
                                    this.validationErrors,
                                    `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                    validationResult.error ||
                                      `${tableField.label ||
                                        "Table Field"} in row ${rowIndex +
                                        1} is not valid`
                                  );
                                  hasError = true;
                                  continue;
                                }

                                // Check claims limit
                                if (validationResult.claimsExceeded) {
                                  console.log(
                                    "🔍 [DEBUG] Table field validation failed - claims limit exceeded"
                                  );
                                  const claimLimit =
                                    validationResult.claimsLimit || 1;
                                  const errorMessage =
                                    claimLimit === 1
                                      ? `${tableField.label ||
                                          "Table Field"} in row ${rowIndex +
                                          1} has already been claimed`
                                      : `${tableField.label ||
                                          "Table Field"} in row ${rowIndex +
                                          1} has reached the claim limit of ${claimLimit}`;

                                  this.$set(
                                    this.validationErrors,
                                    `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                    errorMessage
                                  );
                                  hasError = true;
                                  continue;
                                }

                                console.log(
                                  "🔍 [DEBUG] Adding validation event for table field"
                                );
                                // Add to validation events collection
                                this.validationEvents.push({
                                  dataSetId: tableField.validationDataSet,
                                  recordId: validationResult.recordId,
                                  fieldId: `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                  fieldLabel:
                                    `${field.label} - ${tableField.label}` ||
                                    "Table Field",
                                  value: valueToValidate,
                                  validationPresetRecordSnapshot:
                                    validationResult.recordData
                                });
                                console.log(
                                  "🔍 [DEBUG] Validation events array now has:",
                                  this.validationEvents.length,
                                  "events"
                                );
                              } catch (error) {
                                console.error(
                                  "❌ Error validating table field:",
                                  error
                                );
                                this.$set(
                                  this.validationErrors,
                                  `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                  `Error validating ${tableField.label ||
                                    "Table Field"} in row ${rowIndex + 1}`
                                );
                                hasError = true;
                              }
                            }
                          }
                          // Also check for standard validation structure (fallback)
                          else if (
                            tableField.validation &&
                            tableField.validation.validationFormDataType ===
                              "dynamic" &&
                            tableField.validation.selectedFormValidationDataSet
                          ) {
                            console.log(
                              "🔍 [DEBUG] Table field has standard dynamic validation structure:",
                              tableField.label
                            );

                            const tableFieldId =
                              tableField.idx || `field-${tableField.label}`;
                            const tableFieldValue = row[tableFieldId];

                            console.log(
                              "🔍 [DEBUG] Table field value:",
                              tableFieldValue
                            );

                            if (tableFieldValue) {
                              try {
                                const valueToValidate = this.getIdentifierFromValue(
                                  tableFieldValue
                                );
                                console.log(
                                  "🔍 [DEBUG] Validating table field value:",
                                  valueToValidate
                                );

                                // Use the same validation logic as regular fields
                                const validationResult = await this.$store.dispatch(
                                  "validateTextFieldValue",
                                  {
                                    dataSetId:
                                      tableField.validation
                                        .selectedFormValidationDataSet,
                                    value: valueToValidate,
                                    maxClaims:
                                      tableField.validation.maxClaims || null,
                                    fieldLabel:
                                      `${field.label} - ${tableField.label}` ||
                                      "Table Field"
                                  }
                                );

                                console.log(
                                  "🔍 [DEBUG] Table field validation result:",
                                  validationResult
                                );

                                if (!validationResult.isValid) {
                                  console.log(
                                    "🔍 [DEBUG] Table field validation failed - value is invalid"
                                  );
                                  this.$set(
                                    this.validationErrors,
                                    `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                    validationResult.error ||
                                      `${tableField.label ||
                                        "Table Field"} in row ${rowIndex +
                                        1} is not valid`
                                  );
                                  hasError = true;
                                  continue;
                                }

                                // Check claims limit
                                if (validationResult.claimsExceeded) {
                                  console.log(
                                    "🔍 [DEBUG] Table field validation failed - claims limit exceeded"
                                  );
                                  const claimLimit =
                                    validationResult.claimsLimit || 1;
                                  const errorMessage =
                                    claimLimit === 1
                                      ? `${tableField.label ||
                                          "Table Field"} in row ${rowIndex +
                                          1} has already been claimed`
                                      : `${tableField.label ||
                                          "Table Field"} in row ${rowIndex +
                                          1} has reached the claim limit of ${claimLimit}`;

                                  this.$set(
                                    this.validationErrors,
                                    `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                    errorMessage
                                  );
                                  hasError = true;
                                  continue;
                                }

                                console.log(
                                  "🔍 [DEBUG] Adding validation event for table field (standard structure)"
                                );
                                // Add to validation events collection
                                this.validationEvents.push({
                                  dataSetId:
                                    tableField.validation
                                      .selectedFormValidationDataSet,
                                  recordId: validationResult.recordId,
                                  fieldId: `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                  fieldLabel:
                                    `${field.label} - ${tableField.label}` ||
                                    "Table Field",
                                  value: valueToValidate,
                                  validationPresetRecordSnapshot:
                                    validationResult.recordData
                                });
                                console.log(
                                  "🔍 [DEBUG] Validation events array now has:",
                                  this.validationEvents.length,
                                  "events"
                                );
                              } catch (error) {
                                console.error(
                                  "❌ Error validating table field:",
                                  error
                                );
                                this.$set(
                                  this.validationErrors,
                                  `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                  `Error validating ${tableField.label ||
                                    "Table Field"} in row ${rowIndex + 1}`
                                );
                                hasError = true;
                              }
                            }
                          }
                          // Also check for standard validation structure (fallback)
                          else if (
                            tableField.validation &&
                            tableField.validation.validationFormDataType ===
                              "dynamic" &&
                            tableField.validation.selectedFormValidationDataSet
                          ) {
                            console.log(
                              "🔍 [DEBUG] Table field has standard dynamic validation structure:",
                              tableField.label
                            );

                            const tableFieldId =
                              tableField.idx || `field-${tableField.label}`;
                            const tableFieldValue = row[tableFieldId];

                            console.log(
                              "🔍 [DEBUG] Table field value:",
                              tableFieldValue
                            );

                            if (tableFieldValue) {
                              try {
                                const valueToValidate = this.getIdentifierFromValue(
                                  tableFieldValue
                                );
                                console.log(
                                  "🔍 [DEBUG] Validating table field value:",
                                  valueToValidate
                                );

                                // Use the same validation logic as regular fields
                                const validationResult = await this.$store.dispatch(
                                  "validateTextFieldValue",
                                  {
                                    dataSetId:
                                      tableField.validation
                                        .selectedFormValidationDataSet,
                                    value: valueToValidate,
                                    maxClaims:
                                      tableField.validation.maxClaims || null,
                                    fieldLabel:
                                      `${field.label} - ${tableField.label}` ||
                                      "Table Field"
                                  }
                                );

                                console.log(
                                  "🔍 [DEBUG] Table field validation result:",
                                  validationResult
                                );

                                if (!validationResult.isValid) {
                                  console.log(
                                    "🔍 [DEBUG] Table field validation failed - value is invalid"
                                  );
                                  this.$set(
                                    this.validationErrors,
                                    `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                    validationResult.error ||
                                      `${tableField.label ||
                                        "Table Field"} in row ${rowIndex +
                                        1} is not valid`
                                  );
                                  hasError = true;
                                  continue;
                                }

                                // Check claims limit
                                if (validationResult.claimsExceeded) {
                                  console.log(
                                    "🔍 [DEBUG] Table field validation failed - claims limit exceeded"
                                  );
                                  const claimLimit =
                                    validationResult.claimsLimit || 1;
                                  const errorMessage =
                                    claimLimit === 1
                                      ? `${tableField.label ||
                                          "Table Field"} in row ${rowIndex +
                                          1} has already been claimed`
                                      : `${tableField.label ||
                                          "Table Field"} in row ${rowIndex +
                                          1} has reached the claim limit of ${claimLimit}`;

                                  this.$set(
                                    this.validationErrors,
                                    `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                    errorMessage
                                  );
                                  hasError = true;
                                  continue;
                                }

                                console.log(
                                  "🔍 [DEBUG] Adding validation event for table field (standard structure)"
                                );
                                // Add to validation events collection
                                this.validationEvents.push({
                                  dataSetId:
                                    tableField.validation
                                      .selectedFormValidationDataSet,
                                  recordId: validationResult.recordId,
                                  fieldId: `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                  fieldLabel:
                                    `${field.label} - ${tableField.label}` ||
                                    "Table Field",
                                  value: valueToValidate,
                                  validationPresetRecordSnapshot:
                                    validationResult.recordData
                                });
                                console.log(
                                  "🔍 [DEBUG] Validation events array now has:",
                                  this.validationEvents.length,
                                  "events"
                                );
                              } catch (error) {
                                console.error(
                                  "❌ Error validating table field:",
                                  error
                                );
                                this.$set(
                                  this.validationErrors,
                                  `${fieldId}_row${rowIndex}_${tableFieldId}`,
                                  `Error validating ${tableField.label ||
                                    "Table Field"} in row ${rowIndex + 1}`
                                );
                                hasError = true;
                              }
                            }
                          } else {
                            console.log(
                              "🔍 [DEBUG] Table field has no dynamic validation:",
                              tableField.label
                            );
                          }
                        }
                      }
                    }
                  } else {
                    console.log("🔍 [DEBUG] Table has no data to validate");
                  }

                  // Continue to next field - table validation complete
                  continue;
                }

                // Special handling for dropzone fields
                if (field.type === "dropzone") {
                  console.log("🔍 [DEBUG] Processing dropzone field");
                  // For dropzone fields, they must have at least one file if required
                  if (
                    field.required &&
                    (!value || !Array.isArray(value) || value.length === 0)
                  ) {
                    // Store error in validationErrors object
                    this.$set(
                      this.validationErrors,
                      fieldId,
                      `${field.label || "Files"} are required`
                    );

                    this.$store.commit(
                      "setSnackbar",
                      `${field.label || "Files"} are required`
                    );
                    hasError = true;
                  } else if (this.validationErrors[fieldId]) {
                    // Clear error if field is now valid
                    this.$delete(this.validationErrors, fieldId);
                  }

                  // Continue to next field - special handling complete
                  continue;
                }

                // Required field validation for other field types
                if (field.required && (!value || value === "")) {
                  console.log(
                    "🔍 [DEBUG] Required field validation failed for:",
                    field.label
                  );
                  this.$set(
                    this.validationErrors,
                    fieldId,
                    `${field.label || "Field"} is required`
                  );
                  hasError = true;
                }

                // Skip further validation if no value
                if (!value) {
                  console.log(
                    "🔍 [DEBUG] Skipping further validation - no value for:",
                    field.label
                  );
                  continue;
                }

                console.log(
                  "🔍 [DEBUG] Checking validation type for field:",
                  field.label
                );
                console.log(
                  "🔍 [DEBUG] Field validation object:",
                  field.validation
                );

                // Static validation
                if (
                  field.validation &&
                  field.validation.validationFormDataType === "static" &&
                  field.validation.staticValidationOptions &&
                  field.validation.staticValidationOptions.length > 0
                ) {
                  console.log(
                    "🔍 [DEBUG] Processing static validation for:",
                    field.label
                  );
                  const isValid = field.validation.staticValidationOptions.some(
                    option => option.value === value
                  );

                  if (!isValid) {
                    this.$set(
                      this.validationErrors,
                      fieldId,
                      `${field.label ||
                        "Field"} must match one of the allowed options`
                    );
                    hasError = true;
                  }
                }
                // Dynamic validation
                else if (
                  field.validation &&
                  field.validation.validationFormDataType === "dynamic" &&
                  field.validation.selectedFormValidationDataSet
                ) {
                  console.log(
                    "🔍 [DEBUG] Processing dynamic validation for field:",
                    field.label,
                    "fieldId:",
                    fieldId
                  );
                  console.log("🔍 [DEBUG] Field type:", field.type);
                  console.log("🔍 [DEBUG] Value to validate:", value);

                  try {
                    const dataSetId =
                      field.validation.selectedFormValidationDataSet;
                    const valueToValidate = this.getIdentifierFromValue(value);

                    console.log("🔍 [DEBUG] DataSet ID:", dataSetId);
                    console.log(
                      "🔍 [DEBUG] Value to validate (processed):",
                      valueToValidate
                    );

                    // For text fields, use optimized validation
                    if (field.type === "textfield") {
                      console.log(
                        "🔍 [DEBUG] Using optimized validation for textfield"
                      );

                      const validationResult = await this.$store.dispatch(
                        "validateTextFieldValue",
                        {
                          dataSetId: dataSetId,
                          value: valueToValidate,
                          maxClaims: field.validation.maxClaims || null,
                          fieldLabel: field.label || "Field"
                        }
                      );

                      console.log(
                        "🔍 [DEBUG] Validation result:",
                        validationResult
                      );

                      if (!validationResult.isValid) {
                        console.log(
                          "🔍 [DEBUG] Validation failed - value is invalid"
                        );
                        this.$set(
                          this.validationErrors,
                          fieldId,
                          validationResult.error ||
                            `${field.label || "Field"} is  not valid`
                        );
                        hasError = true;
                        continue;
                      }

                      // Check claims limit
                      if (validationResult.claimsExceeded) {
                        console.log(
                          "🔍 [DEBUG] Validation failed - claims limit exceeded"
                        );
                        const claimLimit = validationResult.claimsLimit || 1;
                        const errorMessage =
                          claimLimit === 1
                            ? `${field.label ||
                                "Field"} has already been claimed`
                            : `${field.label ||
                                "Field"} has reached the claim limit of ${claimLimit}`;

                        this.$set(this.validationErrors, fieldId, errorMessage);
                        hasError = true;
                        continue;
                      }

                      console.log(
                        "🔍 [DEBUG] Adding validation event for textfield"
                      );
                      // Add to validation events collection
                      this.validationEvents.push({
                        dataSetId,
                        recordId: validationResult.recordId,
                        fieldId,
                        fieldLabel: field.label || "Field",
                        value: valueToValidate,
                        validationPresetRecordSnapshot:
                          validationResult.recordData
                      });
                      console.log(
                        "🔍 [DEBUG] Validation events array now has:",
                        this.validationEvents.length,
                        "events"
                      );
                    }
                    // For non-text fields, fall back to original method
                    else {
                      console.log(
                        "🔍 [DEBUG] Using fallback validation for non-textfield:",
                        field.type
                      );

                      let records = this.presetRecordsCache[dataSetId];

                      // If records are not in cache, try to load them
                      if (!records || !Array.isArray(records)) {
                        console.log(
                          "🔍 [DEBUG] Loading preset records from store"
                        );
                        records = await this.$store.dispatch(
                          "getPresetRecords",
                          dataSetId
                        );
                        if (records && Array.isArray(records)) {
                          this.$set(
                            this.presetRecordsCache,
                            dataSetId,
                            records
                          );
                        }
                      }

                      console.log(
                        "🔍 [DEBUG] Preset records loaded:",
                        records ? records.length : 0,
                        "records"
                      );

                      if (!records || !Array.isArray(records)) {
                        console.log("🔍 [DEBUG] No preset records available");
                        this.$set(
                          this.validationErrors,
                          fieldId,
                          `Error: No preset records available for ${field.label ||
                            "Field"}`
                        );
                        hasError = true;
                        continue;
                      }

                      // Find the matching record first
                      const matchingRecord = records.find(
                        record =>
                          record.identifier?.toLowerCase() ===
                          valueToValidate?.toLowerCase()
                      );

                      console.log(
                        "🔍 [DEBUG] Matching record found:",
                        !!matchingRecord
                      );
                      if (matchingRecord) {
                        console.log(
                          "🔍 [DEBUG] Matching record details:",
                          matchingRecord
                        );
                      }

                      if (!matchingRecord) {
                        console.log(
                          "🔍 [DEBUG] Validation failed - no matching record"
                        );
                        this.$set(
                          this.validationErrors,
                          fieldId,
                          `${field.label ||
                            "Field"} is invalid. Please check and enter a valid value.`
                        );
                        hasError = true;
                      } else {
                        console.log(
                          "🔍 [DEBUG] Checking claims limit for non-textfield"
                        );
                        // Check claims limit for non-text fields (same logic as text fields)
                        let claimsExceeded = false;
                        let actualClaimsLimit = matchingRecord.claimsLimit; // First check record level

                        console.log(
                          "🔍 [DEBUG] Record-level claims limit:",
                          actualClaimsLimit
                        );

                        // If no record-level claims limit, check dataset level or field level
                        if (!actualClaimsLimit) {
                          try {
                            const programRef = doc(
                              db(),
                              "programs",
                              this.$store.getters.programId
                            );
                            const dataSetRef = doc(
                              collection(programRef, "presetDataSets"),
                              dataSetId
                            );
                            const dataSetDoc = await getDoc(dataSetRef);
                            if (dataSetDoc.exists()) {
                              const dataSetData = dataSetDoc.data();
                              actualClaimsLimit =
                                dataSetData.claimsLimit ||
                                field.validation.maxClaims;
                            } else {
                              actualClaimsLimit = field.validation.maxClaims;
                            }
                          } catch (error) {
                            console.warn(
                              "Could not fetch dataset claims limit:",
                              error
                            );
                            actualClaimsLimit = field.validation.maxClaims;
                          }
                        }

                        console.log(
                          "🔍 [DEBUG] Final claims limit:",
                          actualClaimsLimit
                        );

                        // Check claims limit if specified
                        if (
                          actualClaimsLimit !== null &&
                          actualClaimsLimit > 0
                        ) {
                          console.log(
                            "🔍 [DEBUG] Checking validation history count"
                          );
                          // Get validation history count for this record
                          const programRef = doc(
                            db(),
                            "programs",
                            this.$store.getters.programId
                          );
                          const validationHistoryRef = collection(
                            doc(
                              collection(programRef, "presetDataSets"),
                              dataSetId,
                              "data",
                              matchingRecord.id
                            ),
                            "validationHistory"
                          );

                          const validationSnapshot = await getDocs(
                            validationHistoryRef
                          );
                          const claimsCount = validationSnapshot.size;
                          claimsExceeded = claimsCount >= actualClaimsLimit;

                          console.log(
                            "🔍 [DEBUG] Current claims count:",
                            claimsCount
                          );
                          console.log(
                            "🔍 [DEBUG] Claims exceeded:",
                            claimsExceeded
                          );
                        }

                        if (claimsExceeded) {
                          console.log(
                            "🔍 [DEBUG] Validation failed - claims limit exceeded for non-textfield"
                          );
                          const claimLimit = actualClaimsLimit || 1;
                          const errorMessage =
                            claimLimit === 1
                              ? `${field.label ||
                                  "Field"} has already been claimed`
                              : `${field.label ||
                                  "Field"} has reached the claim limit of ${claimLimit}`;

                          this.$set(
                            this.validationErrors,
                            fieldId,
                            errorMessage
                          );
                          hasError = true;
                        } else {
                          console.log(
                            "🔍 [DEBUG] Adding validation event for non-textfield"
                          );
                          // Add to validation events collection
                          this.validationEvents.push({
                            dataSetId,
                            recordId: matchingRecord.id,
                            fieldId,
                            fieldLabel: field.label || "Field",
                            value: valueToValidate,
                            validationPresetRecordSnapshot: matchingRecord
                          });
                          console.log(
                            "🔍 [DEBUG] Validation events array now has:",
                            this.validationEvents.length,
                            "events"
                          );
                        }
                      }
                    }
                  } catch (error) {
                    console.error(
                      `❌ Error validating field ${field.label}:`,
                      error
                    );
                    console.error("❌ Error stack:", error.stack);
                    this.$set(
                      this.validationErrors,
                      fieldId,
                      `Error validating ${field.label ||
                        "Field"}. Please enter a valid ${field.label}`
                    );
                    hasError = true;
                  }
                }
              }
            }
          }
        }

        // If any validation errors exist, stop submission process
        if (hasError) {
          console.log("🔍 [DEBUG] Validation failed - stopping submission");
          console.log("🔍 [DEBUG] Validation errors:", this.validationErrors);
          console.log(
            "🔍 [DEBUG] Final validation events count:",
            this.validationEvents.length
          );

          this.hasError = true;
          this.errorMessage =
            "Please complete all required fields before submitting the form.";
          this.processing = false;

          return;
        }

        console.log(
          "🔍 [DEBUG] ========== VALIDATION COMPLETED SUCCESSFULLY =========="
        );
        console.log(
          "🔍 [DEBUG] Final validation events count:",
          this.validationEvents.length
        );
        console.log(
          "🔍 [DEBUG] Final validation events:",
          this.validationEvents
        );

        this.transformFormData();

        // Calculate total points from all tables
        let totalFormPoints = 0;
        const tablePoints = [];

        this.formPages.forEach((page, pageIndex) => {
          page.sections.forEach((section, sectionIndex) => {
            section.columns.forEach((column, columnIndex) => {
              column.items.forEach((field, fieldIndex) => {
                if (field.type === "table" && this.hasPointsColumns(field)) {
                  const fieldId = `field_${pageIndex}_${sectionIndex}_${columnIndex}_${fieldIndex}`;
                  const tableData = this.formValues[fieldId];
                  const points = this.calculateTotalPoints(field, tableData);

                  totalFormPoints += points;
                  tablePoints.push({
                    label: field.label || fieldId,
                    points: points
                  });
                }
              });
            });
          });
        });

        // Update formPoints for display in alert
        this.formPoints = totalFormPoints;

        // Create form submission data structure with complete data
        const formSubmissionData = {
          ...this.structuredFormValues,
          formPoints: totalFormPoints,
          _meta: {
            submittedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            formId: this.currentFormId,
            formTitle:
              this.currentFormTitle || this.formTitle || "Form Submission",
            formVersion: "1.0",
            pointsBreakdown: tablePoints,
            entryId: this.entryId || null,
            status: "submitted"
          },
          _validation: {
            hasValidatedFields: this.validationEvents.length > 0,
            validatedAt: serverTimestamp(),
            validatedFields: this.validationEvents.map(event => ({
              fieldId: event.fieldId,
              fieldLabel: event.fieldLabel,
              dataSetId: event.dataSetId,
              recordId: event.recordId,
              value: event.value,
              validationPath: `programs/${this.$store.getters.programId}/presetDataSets/${event.dataSetId}/data/${event.recordId}/validationHistory`,
              validationSnapshot: event.validationPresetRecordSnapshot || null
            }))
          }
        };

        // Process file resources if needed...
        // ...

        // No need to prepare dates for Firestore since we're using serverTimestamp()
        const preparedData = formSubmissionData;

        // NEW CODE: Call the saveFormEntry action to save to Firestore
        const entryId = await this.$store.dispatch("saveFormEntry", {
          offerId: this.$store.state.offer.currentOffer.id,
          opportunityId: this.opportunityId,
          memberId: this.memberId,
          company: this.$store.getters.currentUser?.company || "",
          formData: preparedData,
          entryId: this.entryId // Pass existing entryId if editing
        });

        // Show a success message
        this.$store.commit("setSnackbar", "Form submitted successfully!");

        // Record validation events if we have any
        if (this.validationEvents.length > 0) {
          console.log(
            "🔍 [DEBUG] Starting validation history recording process"
          );
          console.log(
            "🔍 [DEBUG] Validation events to process:",
            this.validationEvents.length
          );
          console.log(
            "🔍 [DEBUG] Validation events data:",
            this.validationEvents
          );

          // Get the programId from store
          const programId =
            this.$store.state.program?.programId ||
            this.$store.getters.programId;

          console.log("🔍 [DEBUG] Program ID:", programId);

          if (!programId) {
            console.error(
              "❌ Cannot record validation history: programId is missing"
            );
          } else {
            try {
              console.log("🔍 [DEBUG] Processing validation events...");
              // Process each validation event
              for (const validationEvent of this.validationEvents) {
                console.log(
                  "🔍 [DEBUG] Processing validation event:",
                  validationEvent
                );

                try {
                  if (!validationEvent.recordId) {
                    console.error(
                      "❌ Missing recordId in validation event:",
                      validationEvent
                    );
                    continue;
                  }

                  const payload = {
                    ...validationEvent,
                    offerId: this.$store.state.offer?.currentOffer?.id,
                    opportunityId: this.opportunityId,
                    entryId: entryId,
                    memberId: this.memberId
                  };

                  console.log(
                    "🔍 [DEBUG] Calling recordValidationHistory with payload:",
                    payload
                  );

                  const result = await this.$store.dispatch(
                    "recordValidationHistory",
                    payload
                  );

                  console.log(
                    "🔍 [DEBUG] recordValidationHistory result:",
                    result
                  );
                } catch (error) {
                  console.error("❌ Error recording validation:", error);
                  console.error("❌ Error stack:", error.stack);
                }
              }

              // Clear validation events after recording
              this.validationEvents = [];
              console.log("🔍 [DEBUG] Validation events cleared");
            } catch (error) {
              console.error("❌ Error processing validation events:", error);
              console.error("❌ Error stack:", error.stack);
            }
          }
        } else {
          console.log("🔍 [DEBUG] No validation events to record");
        }

        // Emit the event to the parent component (with the entry ID)
        this.$emit("onSubmitForm", { entryId, formData: preparedData });

        // Close the form dialog
        this.closeDialog();
      } catch (error) {
        console.error("[LiveFormEntry] Error submitting form:", error);

        // Try to submit form data even if there was an error with resources
        // (keep existing fallback logic if needed)
        // ...

        this.$store.commit(
          "setSnackbar",
          "Failed to submit form. Please try again."
        );
        this.hasError = true;
        this.errorMessage =
          "Failed to submit form: " + (error.message || "Unknown error");
      } finally {
        this.processing = false;
      }
    },

    handleTableEditStateChange({ fieldId, isEditing }) {
      if (isEditing) {
        if (!this.tablesBeingEdited.includes(fieldId)) {
          this.tablesBeingEdited.push(fieldId);

          // Clear validation errors from the parent form when a table is being edited
          this.clearValidationErrors();
        }
      } else {
        const index = this.tablesBeingEdited.indexOf(fieldId);
        if (index > -1) {
          this.tablesBeingEdited.splice(index, 1);
        }
      }
    },

    handleValidationEvent(validationEvent) {
      console.log(
        "🔍 [DEBUG] handleValidationEvent called with:",
        validationEvent
      );
      // Store successful validation events for recording later
      if (validationEvent && validationEvent.recordId) {
        console.log("🔍 [DEBUG] Adding validation event to array");
        this.validationEvents.push(validationEvent);
        console.log(
          "🔍 [DEBUG] Validation events array now has:",
          this.validationEvents.length,
          "events"
        );
      } else {
        console.log("🔍 [DEBUG] Validation event rejected - missing recordId");
      }
    },

    handleValidationError(errorData) {
      // Handle validation errors from child components
      if (errorData && errorData.fieldId) {
        if (errorData.error) {
          // Set validation error
          this.$set(this.validationErrors, errorData.fieldId, errorData.error);
        } else {
          // Clear validation error if error is null/empty
          if (this.validationErrors[errorData.fieldId]) {
            this.$delete(this.validationErrors, errorData.fieldId);
          }
        }
      }
    },

    // Helper method to clear validation errors
    clearValidationErrors() {
      // Clear all validation errors
      this.validationErrors = {};
    },

    // Add new methods for enhanced form display
    hasPointsColumns(field) {
      return (
        field.componentFields &&
        field.componentFields.some(
          subField =>
            subField.type === "dropdown" &&
            subField.presets &&
            subField.presets.valuesToDisplay &&
            subField.presets.valuesToDisplay.includes("points")
        )
      );
    },

    calculateTotalPoints(field, tableData) {
      if (!Array.isArray(tableData)) return 0;

      let total = 0;
      const pointsFields =
        (field.componentFields &&
          field.componentFields.filter(
            subField =>
              subField.type === "dropdown" &&
              subField.presets &&
              subField.presets.valuesToDisplay &&
              subField.presets.valuesToDisplay.includes("points")
          )) ||
        [];

      // Find quantity field
      const quantityField =
        field.componentFields &&
        field.componentFields.find(
          subField =>
            subField.type === "textfield" && subField.label === "Quantity"
        );

      tableData.forEach(row => {
        pointsFields.forEach(pointsField => {
          const fieldId = pointsField.idx || `field-${pointsField.label}`;
          const pointsKey = `${fieldId}_points`;
          if (row[pointsKey] !== undefined) {
            let points = parseFloat(row[pointsKey]) || 0;

            // If quantity field exists, multiply points by quantity
            if (quantityField) {
              const quantityId =
                quantityField.idx || `field-${quantityField.label}`;
              const quantity = parseFloat(row[quantityId]) || 1;
              points *= quantity;
            }

            total += points;
          }
        });
      });

      return total;
    },

    isPointsEnabledField(field) {
      return (
        field.type === "dropdown" &&
        field.presets &&
        field.presets.presetsType === "dynamic" &&
        field.presets.valuesToDisplay &&
        Array.isArray(field.presets.valuesToDisplay) &&
        field.presets.valuesToDisplay.includes("points")
      );
    },

    getPointsValue(row, field) {
      if (!field) return "0";

      const fieldId = field.idx || `field-${field.label}`;
      const pointsKey = `${fieldId}_points`;

      // Check for stored points value
      if (row[pointsKey] !== undefined) {
        let points = parseFloat(row[pointsKey]) || 0;

        // Find quantity field
        const quantityField =
          field.componentFields &&
          field.componentFields.find(
            subField =>
              subField.type === "textfield" && subField.label === "Quantity"
          );

        // If quantity field exists, multiply points by quantity
        if (quantityField) {
          const quantityId =
            quantityField.idx || `field-${quantityField.label}`;
          const quantity = parseFloat(row[quantityId]) || 1;
          points *= quantity;
        }

        return points.toString();
      }

      const value = row[fieldId];
      if (!value || !field.presets || field.presets.presetsType !== "dynamic") {
        return "0";
      }

      // Try to find points in dynamic options
      const selectedItem = this.findSelectedDynamicItem(field, value);
      if (selectedItem && selectedItem.points !== undefined) {
        let points = parseFloat(selectedItem.points) || 0;

        // Find quantity field
        const quantityField =
          field.componentFields &&
          field.componentFields.find(
            subField =>
              subField.type === "textfield" && subField.label === "Quantity"
          );

        // If quantity field exists, multiply points by quantity
        if (quantityField) {
          const quantityId =
            quantityField.idx || `field-${quantityField.label}`;
          const quantity = parseFloat(row[quantityId]) || 1;
          points *= quantity;
        }

        return points.toString();
      }

      return "0";
    },

    findSelectedDynamicItem(field, value) {
      if (!field || !field.presets || !field.presets.selectedPresetsGroup)
        return null;

      const groupId = field.presets.selectedPresetsGroup;
      const records = this.presetRecordsCache[groupId];

      // Extract identifier if value is an object
      const valueToFind = this.getIdentifierFromValue(value);

      if (records && Array.isArray(records)) {
        return records.find(item => item.identifier === valueToFind);
      }

      return null;
    },

    // Helper method to extract identifier from dropdown value
    getIdentifierFromValue(value) {
      // If value is an object with identifier property, extract it
      if (value !== null && typeof value === "object" && value.identifier) {
        return value.identifier;
      }
      // Otherwise, return the value as is
      return value;
    },

    // Update getFieldValue method to handle points
    getFieldValue(field) {
      if (!field.idx || !this.structuredFormValues[field.idx]) {
        return null;
      }

      // Create a deep copy of the field data
      const fieldData = JSON.parse(
        JSON.stringify(this.structuredFormValues[field.idx])
      );

      // For radio fields, ensure allOptions reflects selection state
      if (field.type === "radio" && fieldData.allOptions) {
        fieldData.allOptions.forEach(opt => {
          opt.isSelected = opt.value === fieldData.value;
        });
      }

      // For table fields, process data and handle points
      if (field.type === "table" && Array.isArray(fieldData.value)) {
        fieldData.value = this.processTableData(field, [...fieldData.value]);

        // Calculate total points if table has points columns
        if (this.hasPointsColumns(field)) {
          fieldData.totalPoints = this.calculateTotalPoints(
            field,
            fieldData.value
          );
        }
      }

      // For dropzone fields, ensure we return file resources
      if (field.type === "dropzone") {
        // If fieldData has value array, use it
        if (Array.isArray(fieldData.value)) {
          return fieldData.value;
        }

        // Look for resources in the form values
        const fieldId = field.fieldId;
        if (fieldId) {
          // Check if this entry has resources for this field
          const entriesCollection =
            this.$store.state.offer.currentOffer?.entries || [];
          const currentEntry = entriesCollection.find(
            entry => entry.id === this.entryId
          );

          if (
            currentEntry &&
            currentEntry[fieldId] &&
            currentEntry[fieldId].resources
          ) {
            return currentEntry[fieldId].resources;
          }
        }

        // Return empty array as fallback
        return [];
      }

      return fieldData.value;
    },

    // Update processTableData method to handle points
    processTableData(tableField, tableData) {
      if (!tableField.componentFields || !Array.isArray(tableData)) {
        return tableData;
      }

      return tableData.map(row => {
        const processedRow = { ...row };

        tableField.componentFields.forEach(field => {
          if (field.type === "dropdown") {
            const fieldId = field.idx || `field-${field.label}`;
            const value = row[fieldId];
            const pointsKey = `${fieldId}_points`;

            // Process dropdown display value
            if (value) {
              processedRow[fieldId] = this.getDropdownDisplayValue(
                field,
                value
              );
            }

            // Include points data if available
            if (row[pointsKey] !== undefined) {
              processedRow[pointsKey] = row[pointsKey];
            }
          }
        });

        return processedRow;
      });
    },

    getDropdownDisplayValue(field, value) {
      if (value === null || value === undefined || value === "") {
        return "Not selected";
      }

      // For dynamic presets
      if (field.presets && field.presets.presetsType === "dynamic") {
        const selectedItem = this.findSelectedDynamicItem(field, value);
        if (selectedItem) {
          return this.generateDisplayText(field, selectedItem);
        } else {
          return value;
        }
      }

      // For static options
      if (field.presets && field.presets.staticOptions) {
        const option = field.presets.staticOptions.find(
          opt => (typeof opt === "object" ? opt.value : opt) === value
        );
        if (option) {
          return typeof option === "object" ? option.text : option;
        }
      }

      return value;
    },

    generateDisplayText(field, item) {
      if (!item) return "-";

      if (
        !field.presets ||
        !field.presets.valuesToDisplay ||
        !field.presets.valuesToDisplay.length
      ) {
        return item.identifier || "-";
      }

      const displayValues = field.presets.valuesToDisplay;

      const displayParts = displayValues
        .map(val => {
          switch (val) {
            case "identifier":
              return item.identifier;
            case "title":
              return item.title;
            case "description":
              return item.description;
            case "points":
              return item.points ? `${item.points} points` : "";
            default:
              return "";
          }
        })
        .filter(Boolean);

      return displayParts.length > 0
        ? displayParts.join(" • ")
        : item.identifier || "-";
    },

    loadFieldResourcesFromEntry(fieldId, idx) {
      return new Promise(resolve => {
        // First check if we can find the resources in the local state
        if (this.entryId) {
          const entriesCollection =
            this.$store.state.offer.currentOffer?.entries || [];
          const currentEntry = entriesCollection.find(
            entry => entry.id === this.entryId
          );

          if (
            currentEntry &&
            currentEntry[fieldId] &&
            currentEntry[fieldId].resources
          ) {
            const resources = currentEntry[fieldId].resources;
            return resolve(resources);
          }

          // If not found in local state, try to load from Firestore
          if (this.$store.state.offer.currentOffer?.id) {
            // Replace Firebase v9 code with v8 syntax
            const entryRef = db
              .collection("programs")
              .doc(this.$store.getters.programId)
              .collection("offers")
              .doc(this.$store.state.offer.currentOffer.id)
              .collection("entries")
              .doc(this.entryId);

            entryRef
              .get()
              .then(snapshot => {
                if (snapshot.exists) {
                  const entryData = snapshot.data();
                  if (entryData[fieldId] && entryData[fieldId].resources) {
                    const resources = entryData[fieldId].resources;
                    return resolve(resources);
                  }
                }
                // If we still haven't found resources, return an empty array
                return resolve([]);
              })
              .catch(error => {
                console.error(
                  `[LiveFormEntry] Error fetching entry from Firestore:`,
                  error
                );
                return resolve([]);
              });
          } else {
            // If no offer ID, return empty array
            return resolve([]);
          }
        } else {
          // If no entry ID, return empty array
          return resolve([]);
        }
      });
    },

    // Add method to clear validation errors for a specific page
    clearPageValidationErrors(pageIndex) {
      const page = this.formPages[pageIndex];
      for (const section of page.sections) {
        for (const column of section.columns) {
          for (const field of column.items) {
            const fieldId = `field_${pageIndex}_${page.sections.indexOf(
              section
            )}_${section.columns.indexOf(column)}_${column.items.indexOf(
              field
            )}`;
            this.$delete(this.validationErrors, fieldId);
          }
        }
      }
      // Clear the validation attempt flag for this step
      this.$delete(this.stepValidationAttempted, pageIndex);
    },

    async validateAndProceed(pageIndex) {
      // Don't validate if any tables are currently being edited
      if (this.tablesBeingEdited.length > 0) {
        this.$store.commit(
          "setSnackbar",
          "Please save or cancel your current table edits before proceeding."
        );
        return;
      }

      // Set validation attempt flag for this step
      this.$set(this.stepValidationAttempted, pageIndex, true);
      this.hasAttemptedSubmit = true; // Set global validation attempt flag

      // Clear previous validation errors for this page
      this.clearPageValidationErrors(pageIndex);

      // Validate fields in the current page
      const page = this.formPages[pageIndex];
      let hasError = false;

      for (const section of page.sections) {
        for (const column of section.columns) {
          for (const field of column.items) {
            const fieldId = `field_${pageIndex}_${page.sections.indexOf(
              section
            )}_${section.columns.indexOf(column)}_${column.items.indexOf(
              field
            )}`;

            // Skip validation for non-input fields
            if (["title", "subtitle", "note"].includes(field.type)) {
              continue;
            }

            const value = this.formValues[fieldId];

            // Special handling for dropzone fields
            if (field.type === "dropzone") {
              // For dropzone fields, they must have at least one file if required
              if (
                field.required &&
                (!value || !Array.isArray(value) || value.length === 0)
              ) {
                // Store error in validationErrors object
                this.$set(
                  this.validationErrors,
                  fieldId,
                  `${field.label || "Files"} are required`
                );
                hasError = true;
              }
              // Continue to next field - special handling complete
              continue;
            }

            // Special handling for table fields
            else if (field.type === "table") {
              // For table fields, they must have at least one row with data if required
              if (
                field.required &&
                (!value ||
                  !Array.isArray(value) ||
                  value.length === 0 ||
                  !value.some(row =>
                    Object.values(row).some(
                      val => val !== undefined && val !== null && val !== ""
                    )
                  ))
              ) {
                // Store error in validationErrors object
                this.$set(
                  this.validationErrors,
                  fieldId,
                  `${field.label || "Table"} requires at least one entry`
                );
                hasError = true;
              }
              // Continue to next field - special handling complete
              continue;
            }

            // Required field validation for other field types
            if (field.required && (!value || value === "")) {
              this.$set(
                this.validationErrors,
                fieldId,
                `${field.label || "Field"} is required`
              );
              hasError = true;
            }

            // Skip further validation if no value
            if (!value) {
              console.log(
                "🔍 [DEBUG] Skipping further validation - no value for:",
                field.label
              );
              continue;
            }

            console.log(
              "🔍 [DEBUG] Checking validation type for field:",
              field.label
            );
            console.log(
              "🔍 [DEBUG] Field validation object:",
              field.validation
            );

            // Static validation
            if (
              field.validation &&
              field.validation.validationFormDataType === "static" &&
              field.validation.staticValidationOptions &&
              field.validation.staticValidationOptions.length > 0
            ) {
              console.log(
                "🔍 [DEBUG] Processing static validation for:",
                field.label
              );
              const isValid = field.validation.staticValidationOptions.some(
                option => option.value === value
              );

              if (!isValid) {
                this.$set(
                  this.validationErrors,
                  fieldId,
                  `${field.label ||
                    "Field"} must match one of the allowed options`
                );
                hasError = true;
              }
            }
            // Dynamic validation
            else if (
              field.validation &&
              field.validation.validationFormDataType === "dynamic" &&
              field.validation.selectedFormValidationDataSet
            ) {
              console.log(
                "🔍 [DEBUG] Processing dynamic validation for field:",
                field.label,
                "fieldId:",
                fieldId
              );
              console.log("🔍 [DEBUG] Field type:", field.type);
              console.log("🔍 [DEBUG] Value to validate:", value);

              try {
                const dataSetId =
                  field.validation.selectedFormValidationDataSet;
                const valueToValidate = this.getIdentifierFromValue(value);

                console.log("🔍 [DEBUG] DataSet ID:", dataSetId);
                console.log(
                  "🔍 [DEBUG] Value to validate (processed):",
                  valueToValidate
                );

                // For text fields, use optimized validation
                if (field.type === "textfield") {
                  console.log(
                    "🔍 [DEBUG] Using optimized validation for textfield"
                  );

                  const validationResult = await this.$store.dispatch(
                    "validateTextFieldValue",
                    {
                      dataSetId: dataSetId,
                      value: valueToValidate,
                      maxClaims: field.validation.maxClaims || null,
                      fieldLabel: field.label || "Field"
                    }
                  );

                  console.log(
                    "🔍 [DEBUG] Validation result:",
                    validationResult
                  );

                  if (!validationResult.isValid) {
                    console.log(
                      "🔍 [DEBUG] Validation failed - value is invalid"
                    );
                    this.$set(
                      this.validationErrors,
                      fieldId,
                      validationResult.error ||
                        `${field.label || "Field"} is  not valid`
                    );
                    hasError = true;
                    continue;
                  }

                  // Check claims limit
                  if (validationResult.claimsExceeded) {
                    console.log(
                      "🔍 [DEBUG] Validation failed - claims limit exceeded"
                    );
                    const claimLimit = validationResult.claimsLimit || 1;
                    const errorMessage =
                      claimLimit === 1
                        ? `${field.label || "Field"} has already been claimed`
                        : `${field.label ||
                            "Field"} has reached the claim limit of ${claimLimit}`;

                    this.$set(this.validationErrors, fieldId, errorMessage);
                    hasError = true;
                    continue;
                  }

                  console.log(
                    "🔍 [DEBUG] Adding validation event for textfield"
                  );
                  // Add to validation events collection
                  this.validationEvents.push({
                    dataSetId,
                    recordId: validationResult.recordId,
                    fieldId,
                    fieldLabel: field.label || "Field",
                    value: valueToValidate,
                    validationPresetRecordSnapshot: validationResult.recordData
                  });
                  console.log(
                    "🔍 [DEBUG] Validation events array now has:",
                    this.validationEvents.length,
                    "events"
                  );
                }
                // For non-text fields, fall back to original method
                else {
                  console.log(
                    "🔍 [DEBUG] Using fallback validation for non-textfield:",
                    field.type
                  );

                  let records = this.presetRecordsCache[dataSetId];

                  // If records are not in cache, try to load them
                  if (!records || !Array.isArray(records)) {
                    console.log("🔍 [DEBUG] Loading preset records from store");
                    records = await this.$store.dispatch(
                      "getPresetRecords",
                      dataSetId
                    );
                    if (records && Array.isArray(records)) {
                      this.$set(this.presetRecordsCache, dataSetId, records);
                    }
                  }

                  console.log(
                    "🔍 [DEBUG] Preset records loaded:",
                    records ? records.length : 0,
                    "records"
                  );

                  if (!records || !Array.isArray(records)) {
                    console.log("🔍 [DEBUG] No preset records available");
                    this.$set(
                      this.validationErrors,
                      fieldId,
                      `Error: No preset records available for ${field.label ||
                        "Field"}`
                    );
                    hasError = true;
                    continue;
                  }

                  // Find the matching record first
                  const matchingRecord = records.find(
                    record =>
                      record.identifier?.toLowerCase() ===
                      valueToValidate?.toLowerCase()
                  );

                  console.log(
                    "🔍 [DEBUG] Matching record found:",
                    !!matchingRecord
                  );
                  if (matchingRecord) {
                    console.log(
                      "🔍 [DEBUG] Matching record details:",
                      matchingRecord
                    );
                  }

                  if (!matchingRecord) {
                    console.log(
                      "🔍 [DEBUG] Validation failed - no matching record"
                    );
                    this.$set(
                      this.validationErrors,
                      fieldId,
                      `${field.label ||
                        "Field"} is invalid. Please check and enter a valid value.`
                    );
                    hasError = true;
                  } else {
                    console.log(
                      "🔍 [DEBUG] Checking claims limit for non-textfield"
                    );
                    // Check claims limit for non-text fields (same logic as text fields)
                    let claimsExceeded = false;
                    let actualClaimsLimit = matchingRecord.claimsLimit; // First check record level

                    console.log(
                      "🔍 [DEBUG] Record-level claims limit:",
                      actualClaimsLimit
                    );

                    // If no record-level claims limit, check dataset level or field level
                    if (!actualClaimsLimit) {
                      try {
                        const programRef = doc(
                          db(),
                          "programs",
                          this.$store.getters.programId
                        );
                        const dataSetRef = doc(
                          collection(programRef, "presetDataSets"),
                          dataSetId
                        );
                        const dataSetDoc = await getDoc(dataSetRef);
                        if (dataSetDoc.exists()) {
                          const dataSetData = dataSetDoc.data();
                          actualClaimsLimit =
                            dataSetData.claimsLimit ||
                            field.validation.maxClaims;
                        } else {
                          actualClaimsLimit = field.validation.maxClaims;
                        }
                      } catch (error) {
                        console.warn(
                          "Could not fetch dataset claims limit:",
                          error
                        );
                        actualClaimsLimit = field.validation.maxClaims;
                      }
                    }

                    console.log(
                      "🔍 [DEBUG] Final claims limit:",
                      actualClaimsLimit
                    );

                    // Check claims limit if specified
                    if (actualClaimsLimit !== null && actualClaimsLimit > 0) {
                      console.log(
                        "🔍 [DEBUG] Checking validation history count"
                      );
                      // Get validation history count for this record
                      const programRef = doc(
                        db(),
                        "programs",
                        this.$store.getters.programId
                      );
                      const validationHistoryRef = collection(
                        doc(
                          collection(programRef, "presetDataSets"),
                          dataSetId,
                          "data",
                          matchingRecord.id
                        ),
                        "validationHistory"
                      );

                      const validationSnapshot = await getDocs(
                        validationHistoryRef
                      );
                      const claimsCount = validationSnapshot.size;
                      claimsExceeded = claimsCount >= actualClaimsLimit;

                      console.log(
                        "🔍 [DEBUG] Current claims count:",
                        claimsCount
                      );
                      console.log(
                        "🔍 [DEBUG] Claims exceeded:",
                        claimsExceeded
                      );
                    }

                    if (claimsExceeded) {
                      console.log(
                        "🔍 [DEBUG] Validation failed - claims limit exceeded for non-textfield"
                      );
                      const claimLimit = actualClaimsLimit || 1;
                      const errorMessage =
                        claimLimit === 1
                          ? `${field.label || "Field"} has already been claimed`
                          : `${field.label ||
                              "Field"} has reached the claim limit of ${claimLimit}`;

                      this.$set(this.validationErrors, fieldId, errorMessage);
                      hasError = true;
                    } else {
                      console.log(
                        "🔍 [DEBUG] Adding validation event for non-textfield"
                      );
                      // Add to validation events collection
                      this.validationEvents.push({
                        dataSetId,
                        recordId: matchingRecord.id,
                        fieldId,
                        fieldLabel: field.label || "Field",
                        value: valueToValidate,
                        validationPresetRecordSnapshot: matchingRecord
                      });
                      console.log(
                        "🔍 [DEBUG] Validation events array now has:",
                        this.validationEvents.length,
                        "events"
                      );
                    }
                  }
                }
              } catch (error) {
                console.error(
                  `❌ Error validating field ${field.label}:`,
                  error
                );
                console.error("❌ Error stack:", error.stack);
                this.$set(
                  this.validationErrors,
                  fieldId,
                  `Error validating ${field.label ||
                    "Field"}. Please enter a valid ${field.label}`
                );
                hasError = true;
              }
            }
          }
        }
      }

      // If there are no validation errors, proceed to next step
      if (!hasError) {
        this.currentStep++;
      } else {
        // Show error message
        this.hasError = true;
        this.errorMessage =
          "Please complete all required fields before proceeding.";
      }
    },

    handleClearValidation(sectionIndex, columnIndex, fieldIndex) {
      // This method can be called in two ways:
      // 1. With specific indices from the old clear validation system
      // 2. Without parameters from the new validation-error system (handled via handleValidationError)

      if (
        sectionIndex !== undefined &&
        columnIndex !== undefined &&
        fieldIndex !== undefined
      ) {
        // Original behavior - find field by indices
        for (
          let pageIndex = 0;
          pageIndex < this.formPages.length;
          pageIndex++
        ) {
          const page = this.formPages[pageIndex];
          if (
            page.sections &&
            page.sections[sectionIndex] &&
            page.sections[sectionIndex].columns &&
            page.sections[sectionIndex].columns[columnIndex] &&
            page.sections[sectionIndex].columns[columnIndex].items &&
            page.sections[sectionIndex].columns[columnIndex].items[fieldIndex]
          ) {
            // Construct the field ID
            const fieldId = `field_${pageIndex}_${sectionIndex}_${columnIndex}_${fieldIndex}`;

            // Get the field value and check if it's valid
            const value = this.formValues[fieldId];
            const field =
              page.sections[sectionIndex].columns[columnIndex].items[
                fieldIndex
              ];

            // Clear validation error if the field has a value (for required fields)
            const isValueEmpty =
              value === null || value === undefined || value === "";

            if (!field.required || !isValueEmpty) {
              if (this.validationErrors[fieldId]) {
                this.$delete(this.validationErrors, fieldId);

                // Clear overall form error if no more validation errors
                if (
                  this.hasError &&
                  Object.keys(this.validationErrors).length === 0
                ) {
                  this.hasError = false;
                  this.errorMessage = "";
                }
              }
            }

            // Field found, no need to continue searching
            return;
          }
        }

        console.warn(
          `[Main] Failed to find field at section ${sectionIndex}, column ${columnIndex}, field ${fieldIndex}`
        );
      }
      // If called without parameters, it's likely from the new validation system
      // The actual clearing is handled by handleValidationError method
    }
  }
};
</script>

<style scoped>
.v-stepper__header {
  -webkit-box-shadow: 0 0px 0px 0px rgb(0 0 0 / 0%), 0 0px 0px 0 rgb(0 0 0 / 0%),
    0 0px 0px 0 rgb(0 0 0 / 0%);
  box-shadow: 0 0px 0px 0px rgb(0 0 0 / 0%), 0 0px 0px 0 rgb(0 0 0 / 0%),
    0 0px 0px 0 rgb(0 0 0 / 0%);
}

.v-sheet.v-stepper:not(.v-sheet--outlined) {
  -webkit-box-shadow: 0 0px 0px 0px rgb(0 0 0 / 0%), 0 0px 0px 0 rgb(0 0 0 / 0%),
    0 0px 0px 0 rgb(0 0 0 / 0%);
  box-shadow: 0 0px 0px 0px rgb(0 0 0 / 0%), 0 0px 0px 0 rgb(0 0 0 / 0%),
    0 0px 0px 0 rgb(0 0 0 / 0%);
  background-color: transparent !important;
}

.v-stepper {
  background-color: transparent !important;
}

.v-stepper__content {
  background-color: transparent !important;
}

.v-stepper__items {
  background-color: transparent !important;
}
</style>
